import { useState, useEffect } from 'react'
import { Search, Filter, Grid, List } from 'lucide-react'
import { anilistService } from '@/services/anilist'
import { Anime } from '@/types/anime'
import { AnimeGrid } from '@/components/AnimeGrid'
import { LoadingSpinner } from '@/components/LoadingSpinner'

const GENRES = [
  'Action', 'Adventure', 'Comedy', 'Drama', 'Ecchi', 'Fantasy', 'Horror',
  'Mahou Shoujo', 'Mecha', 'Music', 'Mystery', 'Psychological', 'Romance',
  'Sci-Fi', 'Slice of Life', 'Sports', 'Supernatural', 'Thriller'
]

const YEARS = Array.from({ length: 25 }, (_, i) => 2024 - i)

const STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'RELEASING', label: 'Ongoing' },
  { value: 'FINISHED', label: 'Completed' },
  { value: 'NOT_YET_RELEASED', label: 'Upcoming' }
]

const SORT_OPTIONS = [
  { value: 'POPULARITY_DESC', label: 'Most Popular' },
  { value: 'SCORE_DESC', label: 'Highest Rated' },
  { value: 'TRENDING_DESC', label: 'Trending' },
  { value: 'START_DATE_DESC', label: 'Newest' },
  { value: 'TITLE_ROMAJI', label: 'A-Z' }
]

export default function Browse() {
  const [animeList, setAnimeList] = useState<Anime[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedGenre, setSelectedGenre] = useState('')
  const [selectedYear, setSelectedYear] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [sortBy, setSortBy] = useState('POPULARITY_DESC')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showFilters, setShowFilters] = useState(false)
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)

  useEffect(() => {
    fetchAnime()
  }, [selectedGenre, selectedYear, selectedStatus, sortBy])

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchQuery.trim()) {
        searchAnime()
      } else {
        fetchAnime()
      }
    }, 500)

    return () => clearTimeout(delayedSearch)
  }, [searchQuery])

  const fetchAnime = async (pageNum = 1) => {
    try {
      setLoading(pageNum === 1)
      
      let results: Anime[] = []
      
      if (selectedGenre) {
        results = await anilistService.getAnimeByGenre(selectedGenre, 20, pageNum)
      } else if (selectedYear) {
        results = await anilistService.getAnimeByYear(parseInt(selectedYear), 20, pageNum)
      } else {
        switch (sortBy) {
          case 'TRENDING_DESC':
            results = await anilistService.getTrendingAnime(20, pageNum)
            break
          case 'SCORE_DESC':
            results = await anilistService.getTopRatedAnime(20, pageNum)
            break
          default:
            results = await anilistService.getPopularAnime(20, pageNum)
        }
      }

      if (pageNum === 1) {
        setAnimeList(results)
      } else {
        setAnimeList(prev => [...prev, ...results])
      }
      
      setHasNextPage(results.length === 20)
      setPage(pageNum)
    } catch (error) {
      console.error('Error fetching anime:', error)
    } finally {
      setLoading(false)
    }
  }

  const searchAnime = async () => {
    try {
      setLoading(true)
      const results = await anilistService.searchAnime(searchQuery, 20)
      setAnimeList(results.Page.media)
      setHasNextPage(results.Page.pageInfo.hasNextPage)
    } catch (error) {
      console.error('Error searching anime:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadMore = () => {
    if (hasNextPage && !loading) {
      fetchAnime(page + 1)
    }
  }

  const clearFilters = () => {
    setSelectedGenre('')
    setSelectedYear('')
    setSelectedStatus('all')
    setSortBy('POPULARITY_DESC')
    setSearchQuery('')
  }

  const hasActiveFilters = selectedGenre || selectedYear || selectedStatus !== 'all' || searchQuery

  return (
    <div className="min-h-screen bg-dark-400 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Browse Anime</h1>
            <p className="text-gray-400">
              Discover thousands of anime series and movies
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-2 mt-4 lg:mt-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search anime..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-dark-300 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Filter Toggle */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
              >
                Clear all filters
              </button>
            )}
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Genre Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Genre
                </label>
                <select
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="w-full p-2 bg-dark-300 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Genres</option>
                  {GENRES.map((genre) => (
                    <option key={genre} value={genre}>
                      {genre}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full p-2 bg-dark-300 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">All Years</option>
                  {YEARS.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full p-2 bg-dark-300 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  {STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sort by
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full p-2 bg-dark-300 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {loading && animeList.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : animeList.length > 0 ? (
          <>
            <div className="mb-6">
              <p className="text-gray-400">
                {searchQuery ? `Search results for "${searchQuery}"` : 'Browse results'} 
                ({animeList.length} anime)
              </p>
            </div>

            <AnimeGrid 
              animeList={animeList} 
              size={viewMode === 'grid' ? 'md' : 'lg'}
            />

            {/* Load More */}
            {hasNextPage && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">
              {searchQuery 
                ? `No anime found for "${searchQuery}"`
                : 'No anime found with the selected filters'
              }
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 text-blue-400 hover:text-blue-300 transition-colors"
              >
                Clear filters and try again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
